import type * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import { TRICK_LABEL, TRICKS, type Trick } from '../commands';

/**
 * Tricks you teach by saying them. Each has a level 0..5; she does it with a
 * probability tied to the level, and a treat given within a few seconds of a
 * success raises the level. Unpractised tricks slowly fade.
 */
export type TrickLevels = Record<Trick, number>;

const LEARN_WITH_TREAT = 1;
const LEARN_WITHOUT = 0.25;
const REWARD_WINDOW = 6; // seconds

export function emptyTricks(): TrickLevels {
  return { spin: 0, shake: 0, rollover: 0, speak: 0, playdead: 0 };
}

export class Tricks {
  levels: TrickLevels;
  onMessage?: (text: string) => void;
  onLearned?: (trick: Trick, level: number) => void;
  /** she did the trick (routines count these) */
  onSuccess?: (trick: Trick) => void;
  onChange?: () => void;
  private rewardable: { trick: Trick; until: number } | null = null;
  private lastAttempt = -Infinity;

  constructor(
    private dog: Dog,
    levels?: TrickLevels,
  ) {
    this.levels = levels ?? emptyTricks();
    dog.onTrick = (name, from) => this.attempt(name, from);
  }

  private now(): number {
    return performance.now() / 1000;
  }

  attempt(trick: Trick, you: THREE.Vector3): boolean {
    if (this.dog.trickActive || this.dog.busy) return true;
    if (this.now() - this.lastAttempt < 1.2) return true;
    this.lastAttempt = this.now();
    const level = this.levels[trick];
    const p = Math.min(1, 0.2 + 0.16 * level);
    const label = TRICK_LABEL[trick];
    if (Math.random() < p) {
      window.setTimeout(() => {
        this.dog.perform(trick, you);
        this.onSuccess?.(trick);
        this.rewardable = { trick, until: this.now() + REWARD_WINDOW };
        this.onMessage?.(level >= 5 ? `${label}! (mastered)` : `${label}! — give a treat to teach it`);
      }, 300 + Math.random() * 400);
    } else {
      window.setTimeout(() => {
        this.dog.confused(you);
        this.onMessage?.(level === 0 ? `"${label}"? she doesn't know that one yet — keep trying` : `"${label}"… she hesitates`);
        // trying still teaches a little
        this.bump(trick, 0.1);
      }, 300 + Math.random() * 300);
    }
    return true;
  }

  /** a treat was given: if a trick just succeeded, it counts as a lesson */
  reward(): boolean {
    const r = this.rewardable;
    if (!r || this.now() > r.until) return false;
    this.rewardable = null;
    this.bump(r.trick, LEARN_WITH_TREAT);
    return true;
  }

  private bump(trick: Trick, amount: number): void {
    const before = Math.floor(this.levels[trick]);
    this.levels[trick] = Math.min(5, this.levels[trick] + amount);
    const after = Math.floor(this.levels[trick]);
    if (after > before) {
      this.onLearned?.(trick, after);
      this.onMessage?.(`${TRICK_LABEL[trick]}: level ${after}${after === 5 ? ' — mastered!' : ''}`);
    }
    this.onChange?.();
  }

  /** successes without a treat still teach, slowly */
  noTreatDecay(): void {
    const r = this.rewardable;
    if (r && this.now() > r.until) {
      this.rewardable = null;
      this.bump(r.trick, LEARN_WITHOUT);
    }
  }

  /** merge from another phone: max per trick */
  merge(other: Partial<TrickLevels>): void {
    for (const t of TRICKS) this.levels[t] = Math.max(this.levels[t], other[t] ?? 0);
    this.onChange?.();
  }

  summary(): string {
    return TRICKS.filter((t) => this.levels[t] >= 1)
      .map((t) => `${TRICK_LABEL[t]} ${Math.floor(this.levels[t])}`)
      .join(' · ');
  }
}
